// src/components/IntroductionSetup.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

const SETUP_STEPS = [
  {
    step: 1,
    key: "company_configured",
    label: "会社マスター登録",
    description: "最初に会社名や基本情報、打刻の丸め設定などを行ってください。",
    path: "/company-config",
    icon: "🏭"
  },
  {
    step: 2,
    key: "locations_configured",
    label: "勤務地マスター登録",
    description: "社員が打刻する勤務地（住所やGPS境界の範囲）を設定します。",
    path: "/locations",
    icon: "📍"
  },
  {
    step: 3,
    key: "departments_configured",
    label: "組織図・部署マスター登録",
    description: "会社の組織構造（部・課・チーム）を登録し、階層を設定します。",
    path: "/departments",
    icon: "🏢"
  },
  {
    step: 4,
    key: "shifts_configured",
    label: "シフトマスター登録",
    description: "出退勤時間の基準となるシフトパターン（定時、夜勤など）を作成します。",
    path: "/shifts",
    icon: "📅"
  },
  {
    step: 5,
    key: "project_master_configured",
    label: "プロジェクトマスター登録",
    description: "プロジェクト名と勤務地（リモート設定を含む）を登録します。社員へのプロジェクト割当やシフト連動に使用します。",
    path: "/admin/project-master",
    icon: "📁"
  },
  {
    step: 6,
    key: "project_patterns_configured",
    label: "プロジェクトパターン登録",
    description: "各プロジェクトの勤務パターンに、上記で作成したシフトを割り当てます（プロジェクトごとの勤務シフト構成）。",
    path: "/admin/project-patterns",
    icon: "🗂️"
  },
  {
    step: 7,
    key: "employees_configured",
    label: "社員マスター登録",
    description: "上記の部署や勤務地の設定完了後、社員情報の登録（またはCSVインポート）を行います。",
    path: "/admin/employee-master",
    icon: "👥"
  },
  {
    step: 8,
    key: "leave_configured",
    label: "有給休暇マスター初期設定",
    description: "登録された社員に対して、現在の有給付与日数や消化日数の初期値を設定します。",
    path: "/paidLeave",
    icon: "🎖️"
  }
];

export default function IntroductionSetup() {
  const navigate = useNavigate();
  
  const [status, setStatus] = useState({
    company_configured: false,
    locations_configured: false,
    departments_configured: false,
    shifts_configured: false,
    project_master_configured: false,
    project_patterns_configured: false,
    employees_configured: false,
    leave_configured: false
  });
  
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchSetupStatus = async () => {
      try {
        setLoading(true);
        const res = await api.get("/setup-status/");
        
        if (res && res.data) {
          // バックエンドからデータベースの取得はエラーが発生するかどうかを確認
          if (res.data.error || res.data.debug_error) {
            setErrorMessage(res.data.error || res.data.debug_error);
          } else {
            // データベース取得できる
            setStatus(res.data);
          }
        }
      } catch (err) {
        console.error("APIの接続エラーが発生しました:", err);
        setErrorMessage(err.message || "サーバが接続できませんでした.");
      } finally {
        setLoading(false);
      }
    };

    fetchSetupStatus();
  }, []);

  return (
    <div className="setup-wizard-page" style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
        <div>
          <h1 style={{ fontSize: "24px", margin: 0 }}>📖 導入ステップガイド (初期設定手順)</h1>
          <p style={{ color: "#64748b", marginTop: "5px" }}>まずは①から順番に、きっちりと作業をお願いします。</p>
        </div>
        <button 
          style={{ padding: "8px 16px", background: "#0099ff", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}
          onClick={() => navigate("/admin/employee-card-setting")}
        >
          戻る
        </button>
      </header>

      {/* エラー表示 */}
      {errorMessage && (
        <div style={{ padding: "12px", background: "#fef2f2", border: "1px solid #fee2e2", color: "#ef4444", borderRadius: "6px", marginBottom: "20px", fontSize: "14px" }}>
          ⚠️ <strong>エラーが発生しました:</strong> {errorMessage}
        </div>
      )}

      {/* データベースloading */}
      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
          🔄 データベースを取得中、お待ちください...
        </div>
      ) : (
        <div className="wizard-timeline" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {SETUP_STEPS.map((item) => {
            
            const isDone = status && typeof status === "object" && item.key in status 
              ? Boolean(status[item.key]) 
              : false;

            return (
              <div 
                key={item.step} 
                style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  background: isDone ? "#f8fafc" : "#f0fdf4", 
                  padding: "16px", 
                  borderRadius: "8px", 
                  border: isDone ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
                  justifyContent: "space-between",
                  transition: "all 0.3s ease"
                }}
              >
                <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
                  {/* 数字 */}
                  <div style={{ 
                    width: "36px", 
                    height: "36px", 
                    borderRadius: "50%", 
                    background: isDone ? "#2563eb" : "#16a34a",
                    color: "#fff", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    fontWeight: "bold",
                    fontSize: isDone ? "16px" : "14px",
                    transition: "background-color 0.3s ease"
                  }}>
                    {item.step}
                  </div>
                  
                  {/* テーマ */}
                  <div>
                    <h3 style={{ margin: 0, fontSize: "16px", color: isDone ? "#14532d" : "#1e293b" }}>
                      {item.icon} {item.label} {isDone && <span style={{ fontSize: "12px", color: "#16a34a", marginLeft: "6px" }}>(登録済み)</span>}
                    </h3>
                    <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#64748b" }}>{item.description}</p>
                  </div>
                </div>

                {/* 遷移ボタン */}
                <button 
                  style={{ 
                    padding: "8px 14px", 
                    background: isDone ? "#2563eb" : "#22c55e",
                    color: "#fff", 
                    border: "none", 
                    borderRadius: "4px", 
                    cursor: "pointer",
                    fontSize: "13px"
                  }}
                  onClick={() => navigate(item.path, { state: { fromWizard: true } })}
                >
                  {isDone ? "確認・編集 →" : "設定画面へ →"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}